import React, { useState } from 'react';
import axiosInstance from '../configurations/axiosInstance/AxiosInstance';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faCalendarDay, faHeart, faHourglassHalf, faSync, faInfoCircle, faVenus } from '@fortawesome/free-solid-svg-icons';

export default function CycleMenstruelSimulator() {
  const [dateDernieresRegles, setDateDernieresRegles] = useState('');
  const [dureeCycle, setDureeCycle] = useState(28);
  const [dureeRegles, setDureeRegles] = useState(5);
  const [nbCycles, setNbCycles] = useState(3);
  const [projections, setProjections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSimulate = async (e) => {
    e.preventDefault();
    if (!dateDernieresRegles) {
      setError('Veuillez sélectionner la date de vos dernières règles.');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const response = await axiosInstance().post('public/menstrual-cycle/simulate', {
        dateDernieresRegles: new Date(dateDernieresRegles),
        dureeCycleMoyenne: parseInt(dureeCycle),
        dureeReglesMoyenne: parseInt(dureeRegles),
        nbCycles: parseInt(nbCycles),
      });
      setProjections(response.data);
    } catch (err) {
      console.error(err);
      if (err.response && err.response.status === 429) {
        setError('Trop de requêtes. Veuillez patienter une minute avant de réessayer.');
      } else {
        setError('Erreur lors du calcul de la simulation. Veuillez vérifier vos entrées.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Helper pour afficher le mois en français
  const formatMonthYear = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-slate-50 to-teal-50/50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* En-tête */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-rose-100 text-rose-500 mb-4 shadow-sm">
            <FontAwesomeIcon icon={faVenus} className="text-3xl" />
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-slate-800 tracking-tight mb-4">
            Calculateur de Cycle & Période Critique
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Calculez gratuitement et anonymement vos prochains cycles, votre date d'ovulation et votre fenêtre fertile (période critique) pour prendre des décisions éclairées.
          </p>
        </div>

        {/* Section principale : Formulaire + Résultats */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Formulaire (Gauche ou Plein écran si pas de résultats) */}
          <div className={`bg-white rounded-3xl border border-slate-100 shadow-xl p-6 sm:p-8 transition-all duration-300 lg:col-span-4`}>
            <h2 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
              <FontAwesomeIcon icon={faCalendarAlt} className="text-rose-500" />
              Vos Paramètres
            </h2>

            <form onSubmit={handleSimulate} className="space-y-6">
              {/* Date des dernières règles */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Date de début des dernières règles
                </label>
                <input
                  type="date"
                  value={dateDernieresRegles}
                  onChange={(e) => setDateDernieresRegles(e.target.value)}
                  className="w-full px-4 py-3 rounded-2xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 text-slate-700 transition-all font-medium"
                />
              </div>

              {/* Curseur Durée du Cycle */}
              <div>
                <div className="flex justify-between text-sm font-semibold text-slate-700 mb-2">
                  <span>Durée moyenne du cycle</span>
                  <span className="text-rose-600 bg-rose-50 px-2 py-0.5 rounded-lg font-bold">{dureeCycle} jours</span>
                </div>
                <input
                  type="range"
                  min="21"
                  max="35"
                  value={dureeCycle}
                  onChange={(e) => setDureeCycle(e.target.value)}
                  className="w-full accent-rose-500 h-2 bg-slate-100 rounded-lg cursor-pointer"
                />
                <div className="flex justify-between text-[11px] text-slate-400 mt-1 font-medium">
                  <span>Court (21j)</span>
                  <span>Moyen (28j)</span>
                  <span>Long (35j)</span>
                </div>
              </div>

              {/* Curseur Durée des règles */}
              <div>
                <div className="flex justify-between text-sm font-semibold text-slate-700 mb-2">
                  <span>Durée moyenne des règles</span>
                  <span className="text-rose-600 bg-rose-50 px-2 py-0.5 rounded-lg font-bold">{dureeRegles} jours</span>
                </div>
                <input
                  type="range"
                  min="3"
                  max="10"
                  value={dureeRegles}
                  onChange={(e) => setDureeRegles(e.target.value)}
                  className="w-full accent-rose-500 h-2 bg-slate-100 rounded-lg cursor-pointer"
                />
                <div className="flex justify-between text-[11px] text-slate-400 mt-1 font-medium">
                  <span>Courte (3j)</span>
                  <span>Moyenne (5j)</span>
                  <span>Longue (10j)</span>
                </div>
              </div>

              {/* Sélecteur de cycles à projeter */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Nombre de cycles à prévoir
                </label>
                <select
                  value={nbCycles}
                  onChange={(e) => setNbCycles(e.target.value)}
                  className="w-full px-4 py-3 rounded-2xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 text-slate-700 bg-white font-medium transition-all"
                >
                  <option value={1}>1 cycle</option>
                  <option value={2}>2 cycles</option>
                  <option value={3}>3 cycles</option>
                  <option value={4}>4 cycles</option>
                  <option value={6}>6 cycles</option>
                </select>
              </div>

              {/* Bouton de soumission */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 px-6 rounded-2xl font-bold text-white bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 shadow-lg shadow-rose-200/50 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSync} className="animate-spin" />
                    Calcul en cours...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCalendarDay} />
                    Simuler mes cycles
                  </>
                )}
              </button>
            </form>

            {/* Message d'erreur */}
            {error && (
              <div className="mt-4 p-4 rounded-2xl bg-red-50 border border-red-100 text-red-600 text-sm font-medium flex items-center gap-2">
                <FontAwesomeIcon icon={faInfoCircle} className="flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </div>

          {/* Résultats (Droite) */}
          <div className="lg:col-span-8 space-y-8">
            {projections.length === 0 ? (
              <div className="bg-white rounded-3xl border border-slate-100 shadow-xl p-8 text-center flex flex-col items-center justify-center h-full min-h-[400px]">
                <div className="w-16 h-16 rounded-full bg-slate-50 text-slate-400 flex items-center justify-center mb-4 border border-dashed border-slate-200">
                  <FontAwesomeIcon icon={faHourglassHalf} className="text-xl" />
                </div>
                <h3 className="text-lg font-bold text-slate-700 mb-2">En attente de simulation</h3>
                <p className="text-sm text-slate-500 max-w-sm">
                  Renseignez vos paramètres de cycle et cliquez sur "Simuler mes cycles" pour afficher vos prévisions personnalisées.
                </p>
              </div>
            ) : (
              <>
                {/* Légende rapide */}
                <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm flex flex-wrap gap-4 justify-around text-xs font-semibold text-slate-600">
                  <div className="flex items-center gap-2">
                    <span className="w-3.5 h-3.5 rounded-full bg-rose-500 shadow-sm shadow-rose-200"></span>
                    <span>Menstruations (Règles)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3.5 h-3.5 rounded-full bg-teal-500 shadow-sm shadow-teal-200"></span>
                    <span>Période fertile (Critique)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3.5 h-3.5 rounded-full bg-yellow-500 shadow-sm shadow-yellow-100"></span>
                    <span>Jour d'ovulation</span>
                  </div>
                </div>

                {/* Liste des cartes de projection */}
                <div className="space-y-6">
                  {projections.map((proj, idx) => (
                    <div 
                      key={idx} 
                      className="bg-white rounded-3xl border border-slate-100 shadow-md hover:shadow-lg transition-all overflow-hidden"
                    >
                      {/* Header de la carte */}
                      <div className="bg-gradient-to-r from-rose-50 to-pink-50/30 px-6 py-4 border-b border-rose-100/30 flex justify-between items-center">
                        <span className="font-bold text-rose-700 text-sm uppercase tracking-wider">
                          Cycle {idx + 1}
                        </span>
                        <span className="text-xs text-slate-400 font-medium capitalize">
                          Prévu en {formatMonthYear(proj.dateDebutRegles)}
                        </span>
                      </div>

                      {/* Corps de la carte */}
                      <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Règles */}
                        <div className="bg-rose-50/40 rounded-2xl p-4 border border-rose-100/50 flex flex-col justify-between">
                          <div>
                            <span className="text-xs font-bold text-rose-500 uppercase tracking-wide block mb-1">Règles prévues</span>
                            <span className="text-sm font-semibold text-slate-800">
                              Du {new Date(proj.dateDebutRegles).getDate()} au {new Date(proj.dateFinRegles).getDate()} {formatMonthYear(proj.dateDebutRegles)}
                            </span>
                          </div>
                          <span className="text-[11px] text-rose-600/80 font-medium mt-2 block">
                            Durée : {Math.round((new Date(proj.dateFinRegles) - new Date(proj.dateDebutRegles)) / (1000 * 60 * 60 * 24)) + 1} jours
                          </span>
                        </div>

                        {/* Période fertile */}
                        <div className="bg-teal-50/40 rounded-2xl p-4 border border-teal-100/50 flex flex-col justify-between">
                          <div>
                            <span className="text-xs font-bold text-teal-600 uppercase tracking-wide block mb-1">Période fertile</span>
                            <span className="text-sm font-semibold text-slate-800">
                              Du {new Date(proj.debutPeriodeFertile).getDate()} au {new Date(proj.finPeriodeFertile).getDate()} {formatMonthYear(proj.debutPeriodeFertile)}
                            </span>
                          </div>
                          <span className="text-[11px] text-teal-700/80 font-medium mt-2 block flex items-center gap-1">
                            <FontAwesomeIcon icon={faHeart} className="text-teal-500" />
                            Probabilité de grossesse élevée
                          </span>
                        </div>

                        {/* Ovulation */}
                        <div className="bg-yellow-50/40 rounded-2xl p-4 border border-yellow-100/50 flex flex-col justify-between">
                          <div>
                            <span className="text-xs font-bold text-yellow-600 uppercase tracking-wide block mb-1">Jour d'ovulation</span>
                            <span className="text-sm font-semibold text-slate-800">
                              {formatDate(proj.dateOvulation)}
                            </span>
                          </div>
                          <span className="text-[11px] text-yellow-700/80 font-medium mt-2 block">
                            Libération de l'ovule (fécondité max)
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Explications médicales pédagogiques */}
                <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-6 sm:p-8 text-white shadow-xl">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon={faInfoCircle} className="text-rose-400" />
                    Comprendre votre cycle menstruel
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-slate-300">
                    <div className="space-y-3">
                      <p>
                        <strong className="text-white">La Phase Folliculaire :</strong> Commence le premier jour des règles et se termine à l'ovulation. Le corps prépare un ovule pour la fécondation.
                      </p>
                      <p>
                        <strong className="text-white">L'Ovulation :</strong> Se produit généralement 14 jours avant le début du cycle suivant. L'ovule est libéré et survit environ 12 à 24 heures.
                      </p>
                    </div>
                    <div className="space-y-3">
                      <p>
                        <strong className="text-white">La Période Critique (Fertile) :</strong> Dure environ 6 jours (5 jours avant l'ovulation et 1 jour après), car les spermatozoïdes peuvent vivre jusqu'à 5 jours dans le système reproducteur féminin.
                      </p>
                      <p>
                        <strong className="text-white">La Phase Lutéale :</strong> Phase post-ovulatoire de 14 jours. S'il n'y a pas fécondation, les taux d'hormones chutent, déclenchant les prochaines règles.
                      </p>
                    </div>
                  </div>
                  <div className="mt-6 pt-4 border-t border-slate-700/50 text-[11px] text-slate-400 text-center">
                    Note : Ce calculateur utilise des formules basées sur des cycles réguliers à des fins de simulation et de sensibilisation. Il ne remplace en aucun cas un avis médical ou un moyen de contraception prescrit par un professionnel de santé.
                  </div>
                </div>
              </>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
